<template>
  <scoped>
    <label :for="id">
      {{ title }}
    </label>
    <input
      :id="id"
      ref="input"
      type="file"
      :multiple="multiple"
      @change="readFiles()"
    >
  </scoped>
</template>

<script setup lang="ts">
  import {ref, useId, useTemplateRef} from "vue"

  defineProps({
    multiple: {
      default: false,
      required: false,
      type: Boolean
    },
    title: {
      default: null,
      required: true,
      type: String
    }
  })

  const files = ref<Array<{
    data: string,
    extension: string,
    name: string
  }>>([])
  const emits = defineEmits(["update"])
  const id = useId()
  const input = useTemplateRef("input")

  defineExpose({
    files
  })

  async function readFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.onload = (event) => {
        let extension: string
        let name: string
        const fileNameSegments = file.name.match(/(.*)\.(.*)/)
        if (fileNameSegments.length && fileNameSegments.length < 3) {
          name = file.name
        } else {
          extension = fileNameSegments[2]
          name = fileNameSegments[1]
        }
        files.value.push({
          data: event.target.result.toString().split(",")[1],
          extension: extension,
          name: name
        })
        resolve()
      }
      fileReader.onerror = (event) => {
        reject(event)
      }
      fileReader.readAsDataURL(file)
    })
  }

  async function readFiles() {
    files.value = []
    for (const file of input.value.files) {
      await readFile(file)
    }
    emits("update")
  }
</script>

<style scoped>
  input[type = "file"] {
    display: none;
  }

  label {
    background-color: var(--theme-color-primary);
    border-color: var(--theme-color-primary);
    border-radius: var(--border-radius-small);
    border-style: solid;
    border-width: var(--border-width-light);
    color: var(--color-white);
    cursor: pointer;
    display: inline-block;
    margin: 0.5em;
    padding: 0.5em;

    &:focus, &:hover {
      background-color: var(--color-grey);
      border-color: var(--color-grey);
    }
  }
</style>
